import { Dispatch, SetStateAction } from 'react';
import { useQuery } from 'react-query';
import {
  enterpriseApplicationsKey,
  errorMessage,
  getEnterpriseApplicationsRoute,
} from '@features/applications';
import axiosInstance from '@server/axiosInstance';
import { Dayjs } from 'dayjs';

type useGetEnterpriseApplicationsProps = {
  status: string;
  pageNo: number;
  date: Dayjs | string | null;
  positions: string[];
  rating: 'All' | number;
  enabled: boolean;
  isWOTCOnly: boolean;
  selectedTag: string
  selectedCompanyId?: string | number;
  selectedInterviewType?: string
  setAlertText?: Dispatch<SetStateAction<string>>;
};

export const useGetEnterpriseApplications = ({
  status,
  date,
  isWOTCOnly,
  pageNo,
  positions,
  rating,
  enabled,
  selectedTag,
  selectedCompanyId,
  selectedInterviewType,
  setAlertText = () => { },
}: useGetEnterpriseApplicationsProps) => {
  const {
    data: applicationsData,
    isFetching: isLoadingApplications,
    refetch: getApplications,
  } = useQuery({
    queryKey: [enterpriseApplicationsKey, selectedInterviewType, status, positions, selectedCompanyId, selectedTag, pageNo],
    queryFn: () => {
      const params = new URLSearchParams();

      if (pageNo) {
        params.append('page', String(pageNo));
      }
      if (status) {
        params.append('status', status);
      }
      if (selectedInterviewType) {
        params.append('interview_action', String(selectedInterviewType));
      }
      if (selectedCompanyId) {
        params.append('company', String(selectedCompanyId));
      }

      if (selectedTag && selectedTag !== 'All') {
        params.append('tags', selectedTag);
      }

      if (date) {
        if (typeof date === 'string') {
          params.append('date', date);
        } else {
          const formattedDate = date.format('YYYY-MM-DD');
          params.append('date', formattedDate);
        }
      }

      if (isWOTCOnly) {
        params.append('wotc', 'true');
      }

      if (positions && positions.length) {
        params.append('position', positions.join(','));
      }

      if (rating && rating !== 'All') {
        params.append('rating', String(rating));
      }

      return axiosInstance
        .get(getEnterpriseApplicationsRoute, { params })
        .then((res) => res.data);
    },
    onSuccess: (data) => {
      if (data.object) {
        setAlertText('');
      }
      if (data?.status !== 200) {
        setAlertText(data?.message);
      }
    },
    onError: () => {
      setAlertText(errorMessage);
    },
    enabled,
    refetchOnWindowFocus: false,
  });

  return {
    getApplications,
    applicationsData,
    isLoadingApplications,
  };
};
